import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { toast } from "@replo/design-system/components/toast/Toast";
import twMerge from "@replo/design-system/utils/twMerge";
import DeleteConfirmationModal from "@v2/components/DeleteConfirmationModal";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { defaultTypography } from "@v2/features/design-library/utils/presets";
import {
  LoadedSettings,
  SettingsLayout,
} from "@v2/features/settings/SettingsCard";
import { useUploadedFontOptions } from "@v2/features/settings/shop/hooks/useUploadedFontOptions";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { fontClient, fontRouter, fontUtils } from "@v2/trpc/routes/font";
import { stylesRouter, stylesUtils } from "@v2/trpc/routes/styles";
import { getProjectLabels } from "@v2/utils/project-labels";
import { useDropzone } from "react-dropzone";
import { LuPlus, LuX } from "react-icons/lu";
import { escapeFontFamilyName } from "replo-utils/lib/font";

interface FontCardProps {
  fontName: string;
  styleCount: number;
  isUploading?: boolean;
  onDelete?: () => void;
}

const FontCard: React.FC<FontCardProps> = ({
  fontName,
  styleCount,
  isUploading = false,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <div
      className="w-full h-[128px] flex flex-col items-center gap-2 relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="w-full flex-1 bg-sheet p-2 rounded-xl border-border border-0.5 relative">
        {!isUploading && onDelete && isHovered && (
          <button
            onClick={onDelete}
            className="absolute top-2 right-2 w-6 h-6 bg-white border border-border rounded-full flex items-center justify-center hover:bg-surface transition-colors z-10"
            aria-label={`Delete ${fontName} font`}
          >
            <LuX size={12} className="text-muted" />
          </button>
        )}
        <div className="flex flex-col items-center justify-center h-full gap-1">
          {isUploading ? (
            <div className="flex items-center justify-center">
              <Spinner className="size-6" variant="default" />
            </div>
          ) : (
            <>
              <div
                className="text-foreground"
                style={{
                  fontFamily: `"${fontName}", "Inter", sans-serif`,
                  fontSize: "48px",
                  fontWeight: "normal",
                  lineHeight: "1",
                }}
              >
                Aa
              </div>
              <div className="text-xs text-muted">
                {styleCount} style{styleCount !== 1 ? "s" : ""}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="text-sm font-medium text-foreground truncate">
        {isUploading ? "Uploading fonts..." : fontName}
      </div>
    </div>
  );
};

const FontCardSkeleton: React.FC = () => {
  return (
    <div className="w-full h-[128px] flex flex-col items-center gap-2">
      <div className="w-full flex-1 bg-sheet p-2 rounded-xl border-border border-0.5">
        <div className="flex flex-col items-center justify-center h-full gap-1">
          <Skeleton className="w-12 h-12 rounded" />
          <Skeleton className="w-16 h-3 rounded" />
        </div>
      </div>
      <Skeleton className="w-20 h-4 rounded" />
    </div>
  );
};

export const useFontUpload = (projectId: string | undefined) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const processFiles = async (files: File[]) => {
    if (!(files && files.length > 0)) {
      return;
    }

    if (!projectId) {
      console.error("No project ID available");
      return;
    }

    setIsUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const allowedTypes = [".woff", ".woff2", ".ttf", ".otf"];
        const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;

        if (!allowedTypes.includes(fileExtension)) {
          toast.error({
            title: "Unsupported file type",
            description: `Replo does not support uploading ${fileExtension}
              files as fonts.`,
          });
          // eslint-disable-next-line replo/no-raw-error-throw
          throw new Error(`Unsupported file type: ${fileExtension}`);
        }

        const { signedUrl, fontId, filename } =
          await fontClient.generateFontUploadUrl.query({
            projectId,
            filename: file.name,
            contentType: file.type,
            contentLength: file.size,
          });

        const uploadResponse = await fetch(signedUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
            "Content-Length": file.size.toString(),
          },
        });

        if (!uploadResponse.ok) {
          // eslint-disable-next-line replo/no-raw-error-throw
          throw new Error(
            `Failed to upload font ${file.name} to Cloudflare: ${uploadResponse.status}`,
          );
        }

        return await fontClient.processFontUpload.mutate({
          projectId,
          fontId,
          filename,
          originalFilename: file.name,
        });
      });

      await Promise.all(uploadPromises);
      await fontUtils.getFonts.invalidate({ projectId });
    } catch (error) {
      console.error("Font upload failed:", error);
      toast.error({
        title: "Upload failed",
        description:
          error instanceof Error ? error.message : "Failed to upload fonts",
      });
    } finally {
      setIsUploading(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      void processFiles(Array.from(files));
    }
  };

  const triggerUpload = () => {
    fileInputRef.current?.click();
  };

  const fileInputElement = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".woff,.woff2,.ttf,.otf"
      multiple
      onChange={handleFileChange}
      style={{ display: "none" }}
    />
  );

  return {
    triggerUpload,
    fileInputElement,
    processFiles,
    isUploading,
  };
};

interface CustomFontsViewProps {
  fontUploadHook: {
    processFiles: (files: File[]) => Promise<void>;
    isUploading: boolean;
  };
  onFontDeleted?: () => void;
}

export const CustomFontsView: React.FC<CustomFontsViewProps> = ({
  fontUploadHook,
  onFontDeleted,
}) => {
  const projectId = useCurrentProjectId();
  const { fonts, isLoading } = useUploadedFontOptions();
  const [fontToDelete, setFontToDelete] = React.useState<string | null>(null);
  const { processFiles, isUploading: isUploadingFonts } = fontUploadHook;

  const { data: styleLibraryData } = stylesRouter.getStyleLibrary.useQuery(
    { projectId: projectId ?? "" },
    { enabled: Boolean(projectId) },
  );

  const { mutateAsync: updateStyleLibrary } =
    stylesRouter.createOrUpdateStyleLibrary.useMutation();

  const fontFaceDeclarations = fonts
    .filter((font) => font.type === "custom")
    .map((font) => {
      return `
        @font-face {
          font-family: "${escapeFontFamilyName(font.name)}";
          src: url("${font.url}") format("woff");
          font-weight: ${font.weight};
          font-style: ${font.style};
          font-display: swap;
        }
      `;
    })
    .join("\n");

  const deleteFontMutation = fontRouter.deleteFontByName.useMutation({
    onSuccess: async () => {
      void fontUtils.getFonts.invalidate({ projectId });

      if (fontToDelete && projectId && styleLibraryData?.styleLibrary) {
        const currentStyleLibrary = styleLibraryData.styleLibrary;
        const typography = currentStyleLibrary.typography;

        if (typography) {
          let needsUpdate = false;
          const updatedTypography = { ...typography };

          (["h1", "h2", "h3", "h4", "body"] as const).forEach((level) => {
            if (typography[level]?.name === fontToDelete) {
              updatedTypography[level] = defaultTypography[level];
              needsUpdate = true;
            }
          });

          if (needsUpdate) {
            await updateStyleLibrary({
              projectId,
              styleLibrary: {
                ...currentStyleLibrary,
                typography: updatedTypography,
              },
            });
            await stylesUtils.getStyleLibrary.invalidate({ projectId });
          }
        }
      }

      onFontDeleted?.();

      setFontToDelete(null);
    },
    onError: (error) => {
      toast.error({
        title: "Failed to delete font",
        description: error.message,
      });
    },
  });

  // Only use dropzone for the drop zone area, not the button
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      // Handle rejected files
      if (fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          const { file, errors } = rejection;
          const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
          errors.forEach((error) => {
            if (error.code === "file-invalid-type") {
              toast.error({
                title: "Unsupported file type",
                description: `Replo does not support uploading ${fileExtension}
                    files as fonts. If this is unexpected, please reach out to support@replo.app.`,
              });
            }
          });
        });
        return;
      }

      // Process accepted files
      void processFiles(acceptedFiles);
    },
    accept: [".woff", ".woff2", ".ttf", ".otf"],
    multiple: true,
    disabled: isUploadingFonts,
  });

  const fontFamilies = React.useMemo(() => {
    const fontFamilyToNumStyles = new Map<string, number>();

    fonts.forEach((font) => {
      const fontName = font.name;
      fontFamilyToNumStyles.set(
        fontName,
        (fontFamilyToNumStyles.get(fontName) ?? 0) + 1,
      );
    });

    const existingFamilies = Array.from(fontFamilyToNumStyles.entries()).map(
      ([name, count]) => ({
        name,
        styleCount: count,
        isUploading: false,
      }),
    );

    return existingFamilies;
  }, [fonts]);

  const hasCustomFonts = fontFamilies.length > 0;

  const handleDeleteFont = async () => {
    if (!fontToDelete || !projectId) {
      return;
    }

    await deleteFontMutation.mutateAsync({
      fontName: fontToDelete,
      projectId,
    });
  };

  return (
    <div className="w-full h-full">
      <style dangerouslySetInnerHTML={{ __html: fontFaceDeclarations }} />
      {fontToDelete && (
        <DeleteConfirmationModal
          assetName={fontToDelete}
          assetTypeDisplayName="Font Family"
          confirmationType="delete"
          extraMessage="This will delete all weights and styles of this font family, and may cause pages which currently use this font family to display incorrectly."
          onDelete={() => void handleDeleteFont()}
          onRequestClose={() => setFontToDelete(null)}
          isLoading={deleteFontMutation.isPending}
        />
      )}
      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pb-6">
          {/* Show 4 skeleton cards while loading */}
          {Array.from({ length: 4 }).map((_, index) => (
            <FontCardSkeleton key={`skeleton-${index}`} />
          ))}
        </div>
      )}
      {!isLoading && (hasCustomFonts || isUploadingFonts) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pb-6">
          {isUploadingFonts && (
            <FontCard
              key="uploading"
              fontName="Uploading..."
              styleCount={0}
              isUploading={true}
            />
          )}
          {fontFamilies.map((font) => (
            <FontCard
              key={font.name}
              fontName={font.name}
              styleCount={font.styleCount}
              isUploading={false}
              onDelete={() => setFontToDelete(font.name)}
            />
          ))}
        </div>
      )}
      {!isLoading && !hasCustomFonts && !isUploadingFonts && (
        <div
          {...getRootProps()}
          className={twMerge(
            "flex h-full w-full flex-col items-center justify-center rounded-lg border-1 border-dashed border-border bg-sheet py-3 px-2 cursor-pointer transition-colors",
            isDragActive && "bg-primary/10 border-primary",
            isUploadingFonts && "opacity-50 cursor-not-allowed",
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              <img
                src="/images/shops/fonts/font-upload-graphic.svg"
                alt="Font upload graphic"
                width={124}
                height={68}
              />
            </div>
            <div className="text-center text-muted-foreground">
              <p className="font-sm">
                <span className="font-semibold">Click to upload</span>{" "}
                <span className="font-normal">or drag and drop</span>
              </p>
              <p className="font-normal">(.woff, .woff2, .ttf, .otf)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const useFontManagerContent = () => {
  const projectId = useCurrentProjectId();
  const fontUploadHook = useFontUpload(projectId);
  const { triggerUpload, fileInputElement } = fontUploadHook;

  const uploadButton = (
    <>
      <Button
        variant="primary"
        size="base"
        startEnhancer={<LuPlus />}
        onClick={triggerUpload}
      >
        Upload Fonts
      </Button>
      {fileInputElement}
    </>
  );

  return {
    uploadButton,
    fontDisplay: (
      <CustomFontsView
        fontUploadHook={{
          processFiles: fontUploadHook.processFiles,
          isUploading: fontUploadHook.isUploading,
        }}
      />
    ),
  };
};

const FontManager: React.FC = () => {
  const { uploadButton, fontDisplay } = useFontManagerContent();
  const { project } = useCurrentProjectContext();
  const labels = getProjectLabels(project?.hostingType);

  const sections = [
    {
      title: `${labels.singularCapitalized} Fonts`,
      actions: uploadButton,
      component: fontDisplay,
    },
  ];

  return (
    <SettingsLayout>
      <LoadedSettings sections={sections} />
    </SettingsLayout>
  );
};

export default FontManager;
